import React, { useEffect, useState } from "react";
import { Alert, Checkbox, CheckboxChangeEvent, Form, Typography } from "antd";
import { getManipulativeAlternativesCoverage } from "../../../../services/manipulative-alternatives/manipulative-alternatives.service";
import type {
  ManipulativeAlternativesCoverage,
  ManipulativeLevelKey,
  ManipulativeSubjectKey,
} from "../../../../services/manipulative-alternatives/manipulative-alternatives.types";

const { Title } = Typography;

export type ManipulativesSwitchField =
  | "kinder_math_without_manipulatives"
  | "kinder_literacy_without_manipulatives"
  | "primaria_math_without_manipulatives"
  | "primaria_literacy_without_manipulatives";

export interface ManipulativesSwitchValues {
  kinder_math_without_manipulatives: boolean;
  kinder_literacy_without_manipulatives: boolean;
  primaria_math_without_manipulatives: boolean;
  primaria_literacy_without_manipulatives: boolean;
}

interface ManipulativesSwitchesProps {
  values: ManipulativesSwitchValues;
  /**
   * Which levels this school actually has. A level it does not teach shows no
   * switches at all — there is nothing to configure for it.
   */
  showKinder: boolean;
  showPrimaria: boolean;
  onChange: (field: ManipulativesSwitchField, checked: boolean) => void;
}

const SUBJECTS: Array<{
  key: ManipulativeSubjectKey;
  short: string;
  full: string;
}> = [
  { key: "math", short: "Mate", full: "Matemáticas" },
  { key: "literacy", short: "Lecto", full: "Lectoescritura" },
];

const LEVELS: Array<{
  key: ManipulativeLevelKey;
  title: string;
}> = [
  { key: "kinder", title: "Kinder" },
  { key: "primaria", title: "Primaria" },
];

const fieldFor = (
  level: ManipulativeLevelKey,
  subject: ManipulativeSubjectKey
): ManipulativesSwitchField =>
  `${level}_${subject}_without_manipulatives` as ManipulativesSwitchField;

/**
 * SCRUM-795 / SCRUM-922: los interruptores que apagan los manipulativos de una
 * asignatura para esta escuela, uno por asignatura y por nivel educativo.
 *
 * Cada uno afecta sólo a su asignatura y a su nivel: Mate cubre los programas
 * de Matemáticas, Lecto cubre Lectoescritura normal y Script, y Kinder nunca
 * toca lo de Primaria. Una escuela puede llevar Mate con manipulativos en
 * Kinder y sin manipulativos en Primaria. Con el interruptor prendido, las
 * actividades marcadas como manipulativas de ese nivel no se muestran y en su
 * lugar aparecen sus alternativas.
 *
 * El aviso no es decorativo. Una actividad manipulativa sin alternativas
 * capturadas desaparece y no deja nada en su lugar, así que el admin tiene que
 * poder ver cuántas son ANTES de guardar, en vez de que lo descubra la escuela.
 * Se cuenta por nivel: prender Kinder no debe avisar sobre actividades de
 * Primaria que ese interruptor nunca va a tocar.
 */
const ManipulativesSwitches: React.FC<ManipulativesSwitchesProps> = ({
  values,
  showKinder,
  showPrimaria,
  onChange,
}) => {
  const [coverage, setCoverage] =
    useState<ManipulativeAlternativesCoverage | null>(null);

  useEffect(() => {
    let active = true;
    getManipulativeAlternativesCoverage().then((data) => {
      if (active) setCoverage(data);
    });
    return () => {
      active = false;
    };
  }, []);

  const visibleLevels = LEVELS.filter(({ key }) =>
    key === "kinder" ? showKinder : showPrimaria
  );

  if (visibleLevels.length === 0) return null;

  const warningFor = (
    level: ManipulativeLevelKey,
    levelTitle: string,
    subject: ManipulativeSubjectKey,
    subjectLabel: string
  ) => {
    const count =
      coverage?.[level]?.[subject].manipulatives_without_alternatives ?? 0;
    if (count === 0) return null;

    return (
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 12 }}
        message={`${count} actividades de ${subjectLabel} de ${levelTitle} no tienen ninguna alternativa capturada`}
        description="Esas actividades no se van a mostrar en esta escuela y no aparecerá nada en su lugar hasta que se les capturen alternativas desde la lección."
      />
    );
  };

  return (
    <>
      {visibleLevels.map(({ key: level, title }) => (
        <React.Fragment key={level}>
          <Title level={5} style={{ marginTop: 15, marginBottom: 8 }}>
            {title}
          </Title>

          {SUBJECTS.map(({ key: subject, short, full }) => {
            const field = fieldFor(level, subject);
            return (
              <React.Fragment key={field}>
                <Form.Item style={{ marginBottom: 8 }}>
                  {/* El nivel va en el aria-label y no en el texto visible: el
                      título de la sección ya lo dice, pero sin él los dos
                      niveles quedarían con dos casillas de nombre idéntico. */}
                  <Checkbox
                    aria-label={`${title} — ${short} sin manipulativos`}
                    checked={values[field]}
                    onChange={(e: CheckboxChangeEvent) =>
                      onChange(field, e.target.checked)
                    }
                  >
                    {`${short} sin manipulativos`}
                  </Checkbox>
                </Form.Item>
                {values[field] && warningFor(level, title, subject, full)}
              </React.Fragment>
            );
          })}
        </React.Fragment>
      ))}
    </>
  );
};

export default ManipulativesSwitches;
